import React from 'react';
import { useDispatch } from 'react-redux';
import { FormattedDate } from 'react-intl';
import { Button, LinearProgress, ListItem, ListItemButton, ListItemText, Typography } from '@mui/material';

import { type FiltersType } from '../../hooks/useFilters';
import useSettings from '../../hooks/useSettings';
import useUserData from '../../hooks/useUserData';
import { executeStored, parseDate } from '../../Utils';
import ResponsiveDialog from '../../dialogs/ResponsiveDialog';

interface FilterListItemProps {
	filter: any;
	onClick: React.MouseEventHandler<HTMLDivElement>;
}

interface FilterLoadDialogProps {
	module: string;
	isOpen: boolean;
	closeDialog: () => void;
	setCurrentFilters: FiltersType['setCurrentFilters'];
}

function FilterListItem({ filter, onClick }: FilterListItemProps) {
	const { t } = useSettings();

	return (
		<ListItem dense disablePadding>
			<ListItemButton onClick={() => onClick(filter.filt_value)}>
				<ListItemText
					slotProps={{
						primary: { className: 'font-600', variant: 'subtitle2' }
					}}
					primary={filter.filt_name}
					secondary={
						filter.filt_d_created ? (
							<FormattedDate
								value={parseDate(filter.filt_d_created)?.toDate()}
								year="numeric"
								month="long"
								day="numeric"
								hour="numeric"
								minute="numeric"
							/>
						) : (
							t('VIEW_ITEM_NO_DATES')
						)
					}
				/>
			</ListItemButton>
		</ListItem>
	);
}

/** This dialog is used to load the current user's saved filters */
export default function FilterLoadDialog({ module, isOpen, closeDialog, setCurrentFilters }: FilterLoadDialogProps) {
	const dispatch = useDispatch();
	const { t } = useSettings();
	const userData = useUserData();

	const [savedFilters, setSavedFilters] = React.useState<any[]>([]);
	const [isLoading, setIsLoading] = React.useState(false);

	function loadFilter(filter) {
		const formattedFilter = filter.map(({ filt_field, filt_clause, filt_value }) => ({
			filt_field,
			filt_clause,
			filt_value: filt_clause === 'in' ? JSON.parse(filt_value) : filt_value
		}));
		setCurrentFilters(state => [...state, ...formattedFilter]);
		closeDialog();
	}

	React.useEffect(() => {
		if (isOpen) {
			setIsLoading(true);
			executeStored({
				action: 'st_sys_filters_saved_list',
				dispatch,
				params: {
					n_page: 1,
					n_rows: 50,
					filters: [
						{ filt_field: 'filt_deleted', filt_clause: 'not_equal', filt_value: 1 },
						{ filt_field: 'filt_module', filt_clause: 'equals', filt_value: module },
						{ filt_field: 'filt_user_uid', filt_clause: 'equals', filt_value: userData.user_uid }
					]
				},
				successHandler: response => {
					setSavedFilters(response.data.list || []);
					setIsLoading(false);
				}
			});
		}
	}, [dispatch, isOpen, module, userData.user_uid]);

	return (
		<ResponsiveDialog
			open={isOpen}
			onClose={closeDialog}
			maxWidth="xs"
			title={t('DIALOG_TITLE_FILTER_LOAD')}
			content={
				isLoading ? (
					<LinearProgress variant="query" className="flex-1" />
				) : savedFilters.length > 0 ? (
					savedFilters.map(filter => (
						<FilterListItem key={filter.filt_id} filter={filter} onClick={loadFilter} />
					))
				) : (
					<Typography className="p-8 italic" align="center" color="textSecondary">
						{t('LIST_NO_ITEMS')}
					</Typography>
				)
			}
			actions={<Button onClick={closeDialog}>{t('DIALOG_ACTION_BACK')}</Button>}
		/>
	);
}
